/**
 * Compliance, in the header — an icon button carrying a dot, opening a popover with the
 * detail the old `ComplianceBadge` pill used to print inline (`● 83% partial | 2 open`).
 *
 * Replaces `ComplianceBadge.tsx`. Reads `useSiteContext()` exactly as the pill did — no new
 * request, same `selected.score` / `selected.band` / `selected.openFindings`.
 *
 * `ic-shield` rather than the rail's `ic-check`. A bare tick beside a dot would assert
 * "all good" on a control that, most of the time, is showing something short of all good —
 * the shield says "posture" without claiming a verdict the colour might contradict. (`ic-check`
 * was tried here first and is wrong for exactly this reason: see the sibling reference this
 * file was built from, whose own comment argues for `ic-shield` and whose code had drifted to
 * `ic-check` regardless. This file follows the argument, not the drift.)
 *
 * THE "COUNT" IS NOT A COUNT. `HeaderStatButton`'s `count` prop exists to gate the quiet
 * state — grey icon, no dot, when there is nothing to flag — and Compliance has no natural
 * integer for that the way Findings has a backlog size. A score is continuous; "0" and
 * "100%" are both real scores, so `selected.score` cannot drive quietness without a compliant
 * site at exactly 0% reading as "nothing to show". So this passes a 0/1 GATE instead: 0 only
 * when the band is `pass` (nothing this control exists to flag), 1 for `warn` or `fail`
 * regardless of the open-finding count, which is shown as its own figure inside the popover
 * rather than reused as the gate.
 *
 * CLICK OPENS A POPOVER, IT NO LONGER NAVIGATES DIRECTLY — same change `FindingsButton` makes,
 * for the same reason: a bare shield icon cannot promise "clicking this leaves the page" the
 * way a labelled pill could. `onOpen` — `setTab('compliance')` — is the explicit "Open
 * Compliance" action at the bottom of the panel instead.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSiteContext } from '@/app/site-context-value';
import { HeaderStatButton, type StatTone } from './HeaderStatButton';
import { ScorePill } from './ScoreRing';
import { Icon } from './Icon';

const BAND_WORD: Record<string, string> = {
  pass: 'compliant',
  warn: 'partial',
  fail: 'non-compliant',
};

const BAND_TONE: Record<string, StatTone> = {
  pass: 'success',
  warn: 'warn',
  fail: 'danger',
};

export function ComplianceButton({
  onOpen,
  className,
}: {
  onOpen: () => void;
  className?: string;
}): ReactNode {
  const { selected, loading } = useSiteContext();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const dismiss = useCallback((): void => setOpen(false), []);
  const close = useCallback((): void => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) dismiss();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, dismiss, close]);

  if (loading) {
    return (
      <div
        className="lm-sk rounded-[var(--radius-sm)]"
        style={{ width: 'var(--header-ctl-h)', height: 'var(--header-ctl-h)' }}
      />
    );
  }
  /* Nothing rather than a placeholder. A shield with no site selected looks like a failed
     fetch; absence is unambiguous — same rule `FindingsButton` follows. */
  if (!selected) return null;

  const tone = BAND_TONE[selected.band] ?? 'muted';
  const wordy = BAND_WORD[selected.band] ?? selected.band;
  const gate = selected.band === 'pass' ? 0 : 1; // see file comment: not a real count

  return (
    <div className="relative" ref={wrap}>
      <HeaderStatButton
        ref={triggerRef}
        icon="ic-shield"
        dot
        count={gate}
        tone={tone}
        label="Compliance"
        detail={`${selected.name}: ${selected.score}% ${wordy}${
          selected.openFindings > 0 ? `, ${selected.openFindings} finding(s) still open` : ''
        }`}
        open={open}
        onClick={() => setOpen((v) => !v)}
        className={className}
      />

      {open && (
        <div
          role="dialog"
          aria-label={`Compliance for ${selected.name}`}
          className="lm-overlay absolute right-0 top-[calc(100%+8px)] z-50 overflow-hidden"
          style={{ width: 268 }}
        >
          <div className="p-[13px_14px]">
            <div
              className="truncate text-[12px] font-bold uppercase tracking-[0.08em]"
              style={{ color: 'var(--muted-fg)' }}
              title={selected.name}
            >
              Compliance — {selected.name}
            </div>

            {/* The pill itself, unchanged — it is still the right shape for this number,
                just relocated from the header chrome into the panel that opens off it. */}
            <div className="mt-[10px]">
              <ScorePill score={selected.score} band={selected.band} />
            </div>

            {/* GUIDELINES §2: omitted rather than printed as zero. */}
            {selected.openFindings > 0 && (
              <div className="mt-[10px] flex items-center justify-between gap-[8px] text-[12.5px]">
                <span style={{ color: 'var(--fg)' }}>Open findings</span>
                <span className="lm-num font-bold" style={{ color: 'var(--warn)' }}>
                  {selected.openFindings}
                </span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              close();
              onOpen();
            }}
            className="lm-hov flex w-full items-center justify-between gap-[8px] p-[11px_14px] text-left text-[12.5px] font-semibold"
            style={{ borderTop: '1px solid var(--border)', color: 'var(--fg)' }}
          >
            Open Compliance
            <Icon name="ic-chevright" size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
